import React from 'react';

// components
import {CardEmptyState, CardError, PageTemplate} from '../../shared/components';
import {LoadingPage} from '../LoadingPage';
import {SectionLoanRequests, SectionLoans} from './sections';

// helpers
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

// services
import {CreditBookApi} from '../../shared/services/ApiService';

// styles
import {IconLoans} from './LoansPage-styles';

// types
import {
  CreditBookApiError,
  GetLoanRequest,
  Loan,
} from '../../shared/services/ApiService/types';

/**
 * Fetch the loans summary
 */
const fetchLoans = async () => {
  const {data} = await CreditBookApi.loan.getLoansSummary();
  return data;
};

/**
 * Fetch the loan requests
 */
const fetchLoanRequests = async () => {
  const {data} = await CreditBookApi.loanRequest.getLoanRequests();
  return data;
};

const LoansPage = () => {
  const {t} = useTranslation();

  const {
    data: loans = [],
    isLoading: loansLoading,
    error: loansError,
    isRefetching: loansRefetching,
  } = useQuery<Loan[], CreditBookApiError>({
    queryKey: ['getLoansSummary'],
    queryFn: fetchLoans,
    initialData: [],
  });

  const {
    data: loanRequests = [],
    isLoading: loanRequestsLoading,
    error: loanRequestsError,
    isRefetching: loanRequestsRefetching,
  } = useQuery<GetLoanRequest[], CreditBookApiError>({
    queryKey: ['getLoanRequests'],
    queryFn: fetchLoanRequests,
    initialData: [],
  });

  const displayLoadingPage =
    loansLoading ||
    loansRefetching ||
    loanRequestsLoading ||
    loanRequestsRefetching;

  /**
   * Render the section for loan requests and request new loan
   */
  const renderSectionLoanRequests = () => {
    if (loanRequestsError) {
      return (
        <CardError
          title={t('loan_loanRequests')}
          message={loanRequestsError?.response?.data?.errorMessage}
        />
      );
    }
    return <SectionLoanRequests loanRequests={loanRequests} />;
  };

  /**
   * Render the section for loans
   */
  const renderSectionLoans = () => {
    if (loansError) {
      return (
        <CardError
          title={t('loan_loans')}
          message={loansError?.response?.data?.errorMessage}
        />
      );
    }
    if (loans.length === 0) {
      return <CardEmptyState text={t('loan_emptyState')} icon={IconLoans} />;
    }
    return <SectionLoans loans={loans} />;
  };

  return (
    <>
      {displayLoadingPage ? (
        <LoadingPage usePortalConfig />
      ) : (
        <PageTemplate title={t('loan_loans')} showWarningBanner={true}>
          {renderSectionLoanRequests()}
          {renderSectionLoans()}
        </PageTemplate>
      )}
    </>
  );
};

export default LoansPage;
